import { ALL_PRODUCT_IDS } from './catalog';
import { hasEntitlement, meetsProductRequirements, ownsProduct } from './entitlements';
import type { EntitlementsMap, ProductId } from './types';

/**
 * Client-only display order for the purchase page. Full Access sits alongside
 * the sequential tiers as an all-at-once alternative rather than a final step.
 */
export const PURCHASE_STEP_ORDER: ProductId[] = ['starter', 'mid_program', 'completion'];

/**
 * The product to offer next: the first tier in order that isn't fully owned.
 * Returns null once the buyer owns everything the sequential path covers.
 */
export function getNextStep(entitlements: EntitlementsMap): ProductId | null {
  return PURCHASE_STEP_ORDER.find((id) => !ownsProduct(entitlements, id)) ?? null;
}

export function getHighestGrantedStep(entitlements: EntitlementsMap): ProductId | null {
  if (ownsProduct(entitlements, 'full_access')) {
    return 'full_access';
  }
  let highest: ProductId | null = null;
  for (const id of PURCHASE_STEP_ORDER) {
    if (ownsProduct(entitlements, id)) {
      highest = id;
    }
  }
  return highest;
}

/**
 * Whether a product should be offered for purchase right now. Prerequisites are
 * a UI affordance only, so this hides tiers the buyer can't sensibly use yet;
 * the server will still fulfill any product it is handed.
 */
export function isProductPurchasable(
  entitlements: EntitlementsMap,
  productId: ProductId,
): boolean {
  return !ownsProduct(entitlements, productId) && meetsProductRequirements(entitlements, productId);
}

export function purchasableProducts(entitlements: EntitlementsMap): ProductId[] {
  return ALL_PRODUCT_IDS.filter((id) => isProductPurchasable(entitlements, id));
}

/**
 * Per-product post-upgrade destination. Assignment-scoped content routes do not
 * exist yet, so upgrades currently land on the assignment hub with an
 * `upgraded` flag; each product gets its own entry here so destinations can be
 * specialized later without touching the checkout call sites.
 */
const UPGRADE_DESTINATION: Partial<Record<ProductId, (assignmentId: number) => string>> = {};

function upgradeDestination(productId: ProductId, assignmentId: number): string {
  const custom = UPGRADE_DESTINATION[productId];
  if (custom) {
    return custom(assignmentId);
  }
  return `/account/payments/${assignmentId}?upgraded=${productId}`;
}

/**
 * Decide where a buyer should land after a successful Stripe checkout, from the
 * assignment's PRE-purchase entitlement state and the product being bought.
 *
 * - New assignment (none selected): returns undefined; the confirm handler on the
 *   subscriptions page routes to the freshly created assignment's onboarding.
 * - Entry purchase on an existing empty assignment (no `pre_assessment` yet):
 *   onboarding, so the athlete can complete setup and take the assessment.
 * - Upgrade (already has `pre_assessment`): a per-product destination
 *   (assignment hub with an `upgraded` flag today).
 */
export function resolveCheckoutReturnTo(params: {
  entitlements: EntitlementsMap;
  productId: ProductId;
  assignmentId?: number;
}): string | undefined {
  const { entitlements, productId, assignmentId } = params;
  if (assignmentId === undefined) {
    return undefined;
  }
  const isUpgrade = hasEntitlement(entitlements, 'pre_assessment');
  if (isUpgrade) {
    return upgradeDestination(productId, assignmentId);
  }
  return `/assignments/${assignmentId}/onboarding`;
}
